/**
 * Main Search Template JavaScript - Simplified
 * Handles search functionality with proper card product integration
 */

class MainSearch {
  constructor() {
    this.searchForm = document.querySelector('.search-form');
    this.searchInput = document.querySelector('.search-input');
    this.searchResultsSection = document.querySelector('.search-results-section');
    this.searchResultsGrid = null; // Will be found dynamically
    this.searchNoResults = document.querySelector('.search-no-results');
    this.searchLoading = document.querySelector('.search-loading');
    
    // Search state
    this.currentQuery = '';
    this.searchTimeout = null;
    this.currentController = null;
    this.isSearching = false;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.handleInitialState();
  }

  bindEvents() {
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', this.handleSubmit.bind(this));
    }

    if (this.searchInput) {
      // Use a simpler approach - only debounce input, not submit
      this.searchInput.addEventListener('input', this.debounce(this.handleInput.bind(this), 300));
      this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
    }
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState.bind(this));
  }

  handleSubmit(event) {
    event.preventDefault();
    const query = this.searchInput.value.trim();
    if (query) {
      this.performSearch(query);
    }
  }

  handleInput(event) {
    const query = event.target.value.trim();
    
    console.log('📝 Input changed to:', query, 'Current query:', this.currentQuery, 'Is searching:', this.isSearching);
    console.log('📝 Event details:', {
      type: event.type,
      target: event.target,
      inputValue: event.target.value,
      inputId: event.target.id,
      inputClass: event.target.className
    });
    
    if (!query) {
      // Only clear if we're not in the middle of a search AND we haven't just completed a search
      if (!this.isSearching && this.currentQuery !== query) {
        console.log('🧹 Clearing results because query is empty');
        console.log('🧹 Stack trace:', new Error().stack);
        this.clearResults();
      } else {
        console.log('🛡️ Skipping clear - either searching or just completed search');
      }
      return;
    }
    
    if (query !== this.currentQuery && query.length >= 2) {
      console.log('🔄 Starting new search because query changed');
      this.performSearch(query);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.searchInput.blur();
      this.clearResults();
    }
  }

  async performSearch(query) {
    console.log('🔍 Starting search for:', query);
    
    // Set searching flag
    this.isSearching = true;
    
    // Cancel previous search
    if (this.currentController) {
      this.currentController.abort();
    }
    
    this.currentController = new AbortController();
    
    try {
      this.showLoading();
      
      const response = await fetch(`/search?q=${encodeURIComponent(query)}&type=product&view=json`, {
        signal: this.currentController.signal
      });
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      const products = data.results || data.products || [];
      
      console.log('📦 Found products:', products.length);
      
      this.currentQuery = query;
      this.displayResults(products, query);
      // Re-enable URL updating for consistency
      this.updateURL(query);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        this.showError('Search failed. Please try again.');
      }
    } finally {
      this.hideLoading();
      this.currentController = null;
      this.isSearching = false; // Clear searching flag
    }
  }

  displayResults(products, query) {
    console.log('📊 Displaying results for:', query, 'Products:', products.length);
    
    this.updateHeader(query, products.length);
    
    if (products.length === 0) {
      this.showNoResults(query);
    } else {
      this.showResults(products);
    }
    
    // Show results section
    if (this.searchResultsSection) {
      this.searchResultsSection.style.display = 'block';
      console.log('📱 Results section made visible');
    }
  }

  async showResults(products) {
    // Find the correct grid to use
    this.findOrCreateGrid();
    
    if (!this.searchResultsGrid) {
      console.log('❌ No grid found, cannot show results');
      return;
    }
    
    console.log('📋 Showing results in grid:', this.searchResultsGrid);
    
    // Hide loading and no results
    this.hideLoading();
    
    if (this.searchNoResults) {
      this.searchNoResults.style.display = 'none';
    }
    
    // Clear grid
    this.searchResultsGrid.innerHTML = '';
    
    // Create all cards first (in parallel)
    console.log('🎯 Creating all cards...');
    const cardPromises = products.map(product => this.createCard(product));
    
    try {
      // Wait for all cards to be created
      const cards = await Promise.all(cardPromises);
      
      // Add all cards to the grid at once
      cards.forEach(card => {
        this.searchResultsGrid.appendChild(card);
      });
      
      console.log('✨ Grid populated with', products.length, 'products all at once');
      
    } catch (error) {
      console.warn('Failed to create some cards:', error);
      // Fallback: try to create cards one by one
      for (const product of products) {
        try {
          const card = await this.createCard(product);
          this.searchResultsGrid.appendChild(card);
        } catch (error) {
          console.warn(`Failed to create card for ${product.title}:`, error);
        }
      }
    }
    
    // Show grid and initialize card functionality
    this.searchResultsGrid.style.display = 'grid';
    this.searchResultsGrid.style.opacity = '1';
    this.searchResultsGrid.style.visibility = 'visible';
    
    // Remove loading state immediately
    this.searchResultsGrid.classList.remove('search-results-grid--loading');
    
    console.log('🎨 Grid styles applied - Display:', this.searchResultsGrid.style.display, 'Opacity:', this.searchResultsGrid.style.opacity);
    
    // Initialize card products after a brief delay
    setTimeout(() => {
      if (window.CardProduct) {
        window.CardProduct.initializeAll();
        console.log('🎯 Card products initialized');
        
        // Verify swatches and chevrons are present and functional
        const swatches = this.searchResultsGrid.querySelectorAll('.card-product__swatch');
        const chevrons = this.searchResultsGrid.querySelectorAll('.card-product__chevron');
        console.log('🎯 Found', swatches.length, 'swatches and', chevrons.length, 'chevrons in grid');
        
        if (swatches.length === 0 && chevrons.length === 0) {
          console.warn('⚠️ No swatches or chevrons found - cards may be using fallback HTML');
        }
      }
      
      // Preserve the search input value after showing results
      if (this.searchInput && this.currentQuery) {
        this.searchInput.value = this.currentQuery;
        console.log('🔒 Preserved input value:', this.currentQuery);
      }
      
      // Double-check that grid is still visible
      if (this.searchResultsGrid) {
        console.log('🔍 Final check - Grid display:', this.searchResultsGrid.style.display, 'Opacity:', this.searchResultsGrid.style.opacity);
        // Force visibility if needed
        if (this.searchResultsGrid.style.display !== 'grid') {
          console.log('⚠️ Grid was hidden, forcing visibility');
          this.searchResultsGrid.style.display = 'grid';
          this.searchResultsGrid.style.opacity = '1';
          this.searchResultsGrid.style.visibility = 'visible';
        }
      }
    }, 100);
  }

  async createCard(product) {
    // Try to use the server-rendered snippet first
    try {
      const handle = product.url.split('/').pop().split('?')[0];
      const showDesc = this.getShowDescription();
      
      const cardUrl = `/products/${handle}?view=card&show_description=${showDesc}`;
      console.log('🎯 Fetching card for:', handle, 'URL:', cardUrl);
      
      const response = await fetch(cardUrl);
      
      console.log('🎯 Card fetch response:', {
        status: response.status,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
        url: response.url
      });
      
      if (response.ok) {
        const html = await response.text();
        console.log('🎯 Card HTML length:', html.length);
        console.log('🎯 Card HTML preview:', html.substring(0, 200));
        console.log('🎯 Card HTML contains swatches:', html.includes('card-product__swatches'));
        console.log('🎯 Card HTML contains chevron:', html.includes('card-product__chevron'));
        
        if (html.trim().length > 50) { // More reasonable minimum
          const wrapper = document.createElement('div');
          wrapper.className = 'product-card-wrapper';
          wrapper.innerHTML = html.trim();
          console.log('🎯 Using server-rendered card with', wrapper.querySelectorAll('*').length, 'elements');
          return wrapper;
        } else {
          console.log('🎯 HTML too short, using fallback');
        }
      } else {
        console.log('🎯 Response not ok:', response.status, response.statusText);
      }
    } catch (error) {
      console.warn('🎯 Snippet fetch failed, using fallback:', error);
    }
    
    // Fallback to simple card
    console.log('🎯 Using fallback card for:', product.title);
    return this.createSimpleCard(product);
  }

  createSimpleCard(product) {
    console.log('🎯 Creating simple card for product:', product);
    
    const wrapper = document.createElement('div');
    wrapper.className = 'product-card-wrapper';
    
    const price = product.price ? (product.price / 100).toFixed(2) : '0.00';
    
    const cardHTML = `
      <div class="card-product" data-product-id="${product.id}">
        <div class="card-product__media-wrapper">
          <a href="${product.url}" class="card-product__media-link">
            ${product.featured_image ? 
              `<img src="${product.featured_image}&width=400" alt="${product.title}" loading="lazy" class="card-product__media">` :
              `<div class="card-product__no-image">No image</div>`
            }
          </a>
        </div>
        <div class="card-product__info">
          <h3 class="card-product__title heading-xs">${product.title}</h3>
          ${this.getShowDescription() && product.body ? 
            `<p class="card-product__desc body">${this.truncateText(product.body, 100)}</p>` : ''
          }
          <p class="card-product__price body">$${price}</p>
        </div>
      </div>
    `;
    
    console.log('🎯 Simple card HTML length:', cardHTML.length);
    wrapper.innerHTML = cardHTML;
    
    return wrapper;
  }

  showNoResults(query) {
    this.findOrCreateGrid();
    
    if (!this.searchNoResults) return;
    
    // Hide loading
    this.hideLoading();
    
    if (this.searchResultsGrid) {
      this.searchResultsGrid.style.display = 'none';
    }
    
    const title = this.searchNoResults.querySelector('.search-no-results-title');
    if (title) {
      title.textContent = `No results found for "${query}"`;
    }
    
    this.searchNoResults.style.display = 'block';
  }

  clearResults() {
    console.log('🧹 Clearing all results');
    this.currentQuery = '';
    
    // Hide loading
    this.hideLoading();
    
    // Reset header
    const title = document.querySelector('.search-title');
    const caption = document.querySelector('.search-caption');
    const count = document.querySelector('.search-results-count');
    
    if (title) title.textContent = 'Search';
    if (caption) caption.textContent = 'Enter a search term to find products';
    if (count) count.textContent = '';
    
    // Clear all grids but DON'T hide them
    const allGrids = document.querySelectorAll('.search-results-grid');
    console.log('🧹 Clearing', allGrids.length, 'grids');
    allGrids.forEach((grid, index) => {
      console.log(`Grid ${index}:`, grid.children.length, 'children');
      grid.innerHTML = '';
      // DON'T set display: none - let showResults handle visibility
      grid.style.opacity = '1';
      grid.style.visibility = 'visible';
      grid.classList.remove('search-results-grid--loading');
    });
    
    if (this.searchNoResults) {
      this.searchNoResults.style.display = 'none';
    }
    
  // DON'T hide the results section or mutate history here; keep state stable
  }

  updateHeader(query, count) {
    const title = document.querySelector('.search-title');
    const caption = document.querySelector('.search-caption');
    const countEl = document.querySelector('.search-results-count');
    
    if (title) title.textContent = `"${query}"`;
    if (caption) caption.textContent = 'Search results for';
    if (countEl) {
      countEl.textContent = count > 0 ? 
        `${count} result${count === 1 ? '' : 's'} found` : 
        'No results found';
    }
  }

  updateURL(query) {
    const url = `/search?q=${encodeURIComponent(query)}&type=product`;
    window.history.pushState({ query }, '', url);
  }

  getShowDescription() {
    const section = document.querySelector('.search-section');
    return section?.dataset.showDescription === 'true';
  }

  truncateText(text, maxLength) {
    const stripped = text.replace(/<[^>]*>/g, '');
    return stripped.length <= maxLength ? stripped : stripped.substr(0, maxLength) + '...';
  }

  showLoading() {
    if (this.searchForm) {
      this.searchForm.classList.add('search-form--loading');
    }
    
    // Show search loading in results section
    if (this.searchLoading) {
      this.searchLoading.style.display = 'flex';
    }
    
    // Hide results grid and no-results while loading
    if (this.searchResultsGrid) {
      this.searchResultsGrid.style.display = 'none';
    }
    if (this.searchNoResults) {
      this.searchNoResults.style.display = 'none';
    }
    
    // Show results section if not visible
    if (this.searchResultsSection) {
      this.searchResultsSection.style.display = 'block';
    }
  }

  hideLoading() {
    if (this.searchForm) {
      this.searchForm.classList.remove('search-form--loading');
    }
    
    // Hide search loading
    if (this.searchLoading) {
      this.searchLoading.style.display = 'none';
    }
  }

  showError(message) {
    // Simple error handling - could be enhanced
    console.error(message);
  }

  findOrCreateGrid() {
    // Always use the first available grid for consistency
    const allGrids = document.querySelectorAll('.search-results-grid');
    console.log('Found grids:', allGrids.length);
    
    if (allGrids.length > 0) {
      // Always use the first grid - this ensures consistency
      console.log('Using first available grid');
      this.searchResultsGrid = allGrids[0];
      console.log('Grid children:', this.searchResultsGrid.children.length, 'Has loading class:', this.searchResultsGrid.classList.contains('search-results-grid--loading'));
    } else {
      // Create a new grid if none exists
      console.log('Creating new grid');
      this.searchResultsGrid = document.createElement('div');
      this.searchResultsGrid.className = 'search-results-grid';
      
      const container = this.searchResultsSection?.querySelector('.container');
      if (container) {
        container.appendChild(this.searchResultsGrid);
      }
    }
    
    console.log('Selected grid:', this.searchResultsGrid);
  }

  handlePopState(event) {
    // Handle browser navigation (back/forward buttons)
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    
    if (query) {
      this.searchInput.value = query;
      if (query !== this.currentQuery) {
        this.performSearch(query);
      }
    } else {
      this.searchInput.value = '';
      this.clearResults();
    }
  }

  handleInitialState() {
    // Handle URL parameters on page load
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    
    if (query && this.searchInput) {
      this.searchInput.value = query;
      this.currentQuery = query;
      
      // Check if there are already products on the page (server-rendered results)
      const existingProducts = document.querySelectorAll('.search-results-grid .product-card-wrapper');
      
      if (existingProducts.length > 0) {
        // Products already exist from server render, don't perform additional search
        console.log('🎯 Found', existingProducts.length, 'existing products, skipping duplicate search');
        
        // Make sure results section is visible
        if (this.searchResultsSection) {
          this.searchResultsSection.style.display = 'block';
        }
        
        // Update header with current results
        this.updateHeader(query, existingProducts.length);
        
      } else {
        // No existing products, perform search (dynamic search)
        console.log('🎯 No existing products found, performing search');
        this.performSearch(query);
      }
    }
    
    // Initialize card product functionality for any existing cards on page load
    setTimeout(() => {
      if (window.CardProduct && typeof window.CardProduct.initializeAll === 'function') {
        window.CardProduct.initializeAll();
        console.log('Card product functionality initialized on page load');
      }
      
      // Also remove loading state from any existing grids
      const allGrids = document.querySelectorAll('.search-results-grid');
      allGrids.forEach(grid => {
        grid.classList.remove('search-results-grid--loading');
        grid.style.opacity = '1';
        grid.style.visibility = 'visible';
      });
    }, 500);
  }

  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const ms = new MainSearch();
  // Safety: ensure any statically rendered grids are visible
  try {
    const resultsSection = document.querySelector('.search-results-section');
    const grids = document.querySelectorAll('.search-results-grid');
    grids.forEach((g) => {
      g.classList.remove('search-results-grid--loading');
      g.style.opacity = '1';
      g.style.visibility = 'visible';
      if (g.children.length > 0) {
        g.style.display = 'grid';
        if (resultsSection) resultsSection.style.display = 'block';
      }
    });
  } catch (e) {
    console.warn('Search init visibility guard failed:', e);
  }
});

window.MainSearch = MainSearch;
